import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { FileText, RefreshCw } from 'lucide-react';
import { useState, use } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BIBLE_BOOKS } from '@/lib/utils/bible-utils';
import { AppDataContext } from '@/lib/app-data-hydration';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { getPassagesByBookMap } from '@/data/passage/passage-data-service';

export default function AdminPassages() {
    const [isGeneratingAllSections, setIsGeneratingAllSections] = useState(false);
    const [model, setModel] = useState<string>('gemini-3-flash-preview');
    const [currentBook, setCurrentBook] = useState<string>('');
    const [completedBooks, setCompletedBooks] = useState<string[]>([]);
    const [failedBooks, setFailedBooks] = useState<string[]>([]);
    const [regeneratingBooks, setRegeneratingBooks] = useState<Set<string>>(new Set());

    const { data: sectionData } = use(PassageDataContext);
    const { refresh } = use(AppDataContext);

    // Get data maps
    const sectionsByBook = sectionData ? getPassagesByBookMap(sectionData.items) : new Map();

    const handleGenerateAllSections = async () => {
        if (!confirm('Are you sure?') || !confirm('Are you really really sure?')) {
            return;
        }

        setIsGeneratingAllSections(true);
        setCompletedBooks([]);
        setFailedBooks([]);
        setCurrentBook('');

        const functions = getFunctions();
        const sectionGeneration = httpsCallable(functions, 'sectionGeneration', {
            timeout: 600000 // 10 minutes
        });

        // Only process books that have no sections
        const booksToProcess = BIBLE_BOOKS.filter(bookInfo => (sectionsByBook.get(bookInfo.name) || []).length === 0);

        for (const bookInfo of booksToProcess) {
            const book = bookInfo.name;
            setCurrentBook(book);
            try {
                console.log(`Generating sections for ${book} with model ${model}...`);
                const result = await sectionGeneration({ book, model });
                console.log(`Successfully generated sections for ${book}:`, result);
                setCompletedBooks(prev => [...prev, book]);
            } catch (error) {
                console.error(`Failed to generate sections for ${book}:`, error);
                setFailedBooks(prev => [...prev, book]);
            }
        }

        setCurrentBook('');
        setIsGeneratingAllSections(false);
        await refresh();
    };

    const handleRegenerateBook = async (book: string) => {
        if (!confirm('Are you sure?') || !confirm('Are you really really sure?')) {
            return;
        }

        setRegeneratingBooks(prev => new Set(prev).add(book));

        const functions = getFunctions();
        const generation = httpsCallable(functions, 'sectionGeneration', {
            timeout: 600000 // 10 minutes
        });

        try {
            console.log(`Regenerating sections for ${book} with model ${model}...`);
            const result = await generation({ book, model });
            console.log(`Successfully regenerated sections for ${book}:`, result);
            await refresh();
        } catch (error) {
            console.error(`Failed to regenerate sections for ${book}:`, error);
        } finally {
            setRegeneratingBooks(prev => {
                const newSet = new Set(prev);
                newSet.delete(book);
                return newSet;
            });
        }
    };

    // Calculate books that need section generation
    const booksNeedingSections = BIBLE_BOOKS.filter(bookInfo => (sectionsByBook.get(bookInfo.name) || []).length === 0);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Bible Passage Generation
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    Convert verses to paragraph-based passages (required first step)
                </p>
            </CardHeader>
            <CardContent>
                {/* Model Selection */}
                <div className="flex items-center gap-2 mb-6">
                    <span className="text-sm font-medium">Model:</span>
                    <Select value={model} onValueChange={setModel}>
                        <SelectTrigger className="w-64">
                            <SelectValue placeholder="Select model" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                            <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                            <SelectItem value="gemini-3-flash-preview">Gemini 3.0 Flash</SelectItem>
                            <SelectItem value="gemini-3-pro-preview">Gemini 3.0 Pro</SelectItem>
                            <SelectItem value="gemini-3.1-pro-preview">Gemini 3.1 Pro</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Bulk Generation Control */}
                <div className="mb-6">
                    <Button
                        onClick={handleGenerateAllSections}
                        disabled={isGeneratingAllSections || booksNeedingSections.length === 0}
                        className="w-full"
                        variant={booksNeedingSections.length > 0 ? "default" : "outline"}
                    >
                        {isGeneratingAllSections ? (
                            <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Generating Passages...
                            </>
                        ) : (
                            <>
                                <FileText className="h-4 w-4 mr-2" />
                                Generate All Passages ({booksNeedingSections.length})
                            </>
                        )}
                    </Button>
                </div>

                {/* Progress Display */}
                {isGeneratingAllSections && (
                    <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>Progress:</span>
                                <span>{completedBooks.length + failedBooks.length} / {BIBLE_BOOKS.length}</span>
                            </div>
                            {currentBook && (
                                <p className="text-sm text-muted-foreground">
                                    Currently processing: <strong>{currentBook}</strong>
                                </p>
                            )}
                            {completedBooks.length > 0 && (
                                <div className="text-xs text-green-600">
                                    Completed: {completedBooks.join(', ')}
                                </div>
                            )}
                            {failedBooks.length > 0 && (
                                <div className="text-xs text-red-600">
                                    Failed: {failedBooks.join(', ')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Individual Book Management */}
                <div className="mt-6">
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {BIBLE_BOOKS.map(bookInfo => {
                            const book = bookInfo.name;
                            const sections = sectionsByBook.get(book) || [];
                            const sectionsCount = sections.length;
                            const isRegenerating = regeneratingBooks.has(book);

                            return (
                                <div key={book} className="flex items-center justify-between p-3 rounded border bg-card">
                                    <div className="font-medium text-sm">
                                        {book}
                                    </div>

                                    <Button
                                        size="sm"
                                        variant={sectionsCount === 0 ? "default" : "outline"}
                                        onClick={() => handleRegenerateBook(book)}
                                        disabled={isGeneratingAllSections || isRegenerating}
                                    >
                                        {isRegenerating ? (
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                            <FileText className="h-3 w-3 mr-1" />
                                        )}
                                        Passages ({sectionsCount})
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

